"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui";
import { Label } from "@/components/ui";
import { Checkbox } from "@/components/ui";
import { Alert } from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui";
import Link from "next/link";
import { toast } from "sonner";
import { signupUser } from "@/api/auth";
import { AlertTriangle, X } from "lucide-react";

const signupSchema = z
  .object({
    schoolCode: z
      .string()
      .min(5, "학교 코드를 입력해주세요.")
      .length(5, "학교 코드는 5자리입니다.")
      .regex(/^\d{5}$/, "학교 코드는 숫자 5자리입니다."),
    name: z.string().min(1, "성명을 입력해주세요.").trim(),
    title: z.string().min(1, "직급을 선택해주세요."),
    phoneNumber: z
      .string()
      .min(1, "전화번호를 입력해주세요.")
      .regex(/^[0-9-]+$/, "전화번호 형식이 올바르지 않습니다."),
    password: z
      .string()
      .min(1, "비밀번호를 입력해주세요.")
      .min(8, "비밀번호는 8자리 이상이어야 합니다.")
      .regex(
        /^(?=.*[a-zA-Z])(?=.*[0-9])/,
        "비밀번호는 영문과 숫자를 포함해야 합니다."
      ),
    confirmPassword: z.string().min(1, "비밀번호 확인을 입력해주세요."),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: "이용약관에 동의해주세요.",
    }),
    agreeToPrivacy: z.boolean().refine((val) => val === true, {
      message: "개인정보처리방침에 동의해주세요.",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다.",
    path: ["confirmPassword"],
  });

type SignupFormData = z.infer<typeof signupSchema>;

export default function SignupPage() {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      schoolCode: "",
      name: "",
      title: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
      agreeToPrivacy: false,
    },
  });

  const schoolCodeValue = watch("schoolCode");
  const watchedTitle = watch("title");
  const watchedName = watch("name");
  const watchedPhoneNumber = watch("phoneNumber");
  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");
  const watchedAgreeToTerms = watch("agreeToTerms");
  const watchedAgreeToPrivacy = watch("agreeToPrivacy");

  const isFormValid =
    schoolCodeValue &&
    watchedName &&
    watchedTitle &&
    watchedPhoneNumber &&
    watchedPassword &&
    watchedConfirmPassword &&
    watchedAgreeToTerms &&
    watchedAgreeToPrivacy;

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7)
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    if (phoneNumberLength < 11)
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
        3,
        7
      )}-${phoneNumber.slice(7)}`;
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
      3,
      7
    )}-${phoneNumber.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    const phoneNumber = inputValue.replace(/[^\d]/g, "");

    const formatted = formatPhoneNumber(phoneNumber);

    let newCursorPosition = cursorPosition;

    if (inputValue !== formatted) {
      const digitsBeforeCursor = inputValue
        .slice(0, cursorPosition)
        .replace(/[^\d]/g, "").length;

      let digitCount = 0;
      newCursorPosition = 0;

      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
          digitCount++;
          if (digitCount === digitsBeforeCursor) {
            newCursorPosition = i + 1;
            break;
          }
        }
        newCursorPosition = i + 1;
      }
    }

    setValue("phoneNumber", formatted);

    setTimeout(() => {
      e.target.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const onSubmit = async (data: SignupFormData) => {
    try {
      setIsSubmitting(true);

      // API 호출을 위한 데이터 변환
      const signupData = {
        schoolCode: data.schoolCode.length === 5
          ? `${data.schoolCode[0]}-${data.schoolCode.slice(1)}`
          : data.schoolCode,
        name: data.name,
        title: data.title,
        phone: data.phoneNumber,
        password: data.password,
        confirmPassword: data.confirmPassword,
        agreeToTerms: data.agreeToTerms,
        agreeToPrivacy: data.agreeToPrivacy,
      };

      const result = await signupUser(signupData);

      if (result.success) {
        toast.success(result.message);
        router.push("/login");
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "회원가입에 실패했습니다.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">회원가입</h1>
          <p className="mt-2 text-sm text-gray-600">
            신입생 관리 시스템에 가입하시려면 아래 정보를 입력해주세요.
          </p>
        </div>

        <div className="space-y-3">
          <Alert.Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <Alert.Description className="text-amber-800">
              알림톡이나 문자를 받은 경우 회원가입 없이 로그인 하시면 됩니다.
            </Alert.Description>
          </Alert.Alert>

          <Alert.Alert className="border-red-200 bg-red-50">
            <X className="h-4 w-4 text-red-600" />
            <Alert.Description className="text-red-800">
              <div className="space-y-1">
                <p>허위 정보 또는 타인의 정보로 회원가입 시</p>
                <p>[정보통신망 이용촉진 및 정보보호 등에 관한 법률] 및</p>
                <p>[형법 제314조(업무방해)]에 의거하여 처벌받을 수 있습니다.</p>
              </div>
            </Alert.Description>
          </Alert.Alert>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* 학교 코드 */}
          <div className="space-y-2">
            <Label htmlFor="school_code" className="required">
              학교 코드
            </Label>
            <div className="flex justify-center w-full">
              <InputOTP
                maxLength={5}
                value={schoolCodeValue}
                onChange={(value) => setValue("schoolCode", value, { shouldValidate: true })}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {errors.schoolCode && (
              <p className="text-red-500 text-sm">
                {errors.schoolCode.message}
              </p>
            )}
            <p className="text-muted-foreground text-sm">
              운영자에게 받은 학교 코드 5자리를 입력해주세요.
            </p>
          </div>

          {/* 성명 */}
          <div className="space-y-2">
            <Label htmlFor="name" className="required">
              성명
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="성명을 입력해주세요"
              {...register("name")}
              required
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
            <p className="text-muted-foreground text-sm">
              동명이인이 있다면 운영자에게 받은 성명을 입력해주세요.
            </p>
          </div>

          {/* 직급 */}
          <div className="space-y-2">
            <Label htmlFor="position" className="required">
              직급
            </Label>
            <Select
              value={watchedTitle}
              onValueChange={(value) => setValue("title", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="직급을 선택해주세요" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="교장">교장</SelectItem>
                <SelectItem value="교감">교감</SelectItem>
                <SelectItem value="교사">교사</SelectItem>
              </SelectContent>
            </Select>
            {errors.title && (
              <p className="text-red-500 text-sm">{errors.title.message}</p>
            )}
          </div>

          {/* 전화번호 */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="required">
              전화번호
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="전화번호를 입력해주세요"
              value={watchedPhoneNumber}
              onChange={handlePhoneChange}
              maxLength={13}
              required
            />
            {errors.phoneNumber && (
              <p className="text-red-500 text-sm">
                {errors.phoneNumber.message}
              </p>
            )}
          </div>

          {/* 비밀번호 */}
          <div className="space-y-2">
            <Label htmlFor="password" className="required">
              비밀번호
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="비밀번호를 입력해주세요"
              {...register("password")}
              required
            />
            {errors.password && (
              <p className="text-red-500 text-sm">{errors.password.message}</p>
            )}
            <p className="text-muted-foreground text-sm">
              영문과 숫자를 포함하여 8자리 이상 입력해주세요.
            </p>
          </div>

          {/* 비밀번호 확인 */}
          <div className="space-y-2">
            <Label htmlFor="confirm_password" className="required">
              비밀번호 확인
            </Label>
            <Input
              id="confirm_password"
              type="password"
              placeholder="비밀번호를 다시 입력해주세요"
              {...register("confirmPassword")}
              required
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* 약관 동의 */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="agree_terms"
                checked={watch("agreeToTerms")}
                onCheckedChange={(checked) =>
                  setValue("agreeToTerms", checked as boolean)
                }
              />
              <Label htmlFor="agree_terms" className="text-sm">
                <Link
                  href="/terms"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  이용약관
                </Link>
                에 동의합니다.
              </Label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-red-500 text-sm">
                {errors.agreeToTerms.message}
              </p>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="agree_privacy"
                checked={watch("agreeToPrivacy")}
                onCheckedChange={(checked) =>
                  setValue("agreeToPrivacy", checked as boolean)
                }
              />
              <Label htmlFor="agree_privacy" className="text-sm">
                <Link
                  href="/privacy"
                  className="underline underline-offset-4 hover:text-primary"
                >
                  개인정보처리방침
                </Link>
                에 동의합니다.
              </Label>
            </div>
            {errors.agreeToPrivacy && (
              <p className="text-red-500 text-sm">
                {errors.agreeToPrivacy.message}
              </p>
            )}
          </div>

          {/* 회원가입 버튼 */}
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? "회원가입 중..." : "회원가입"}
          </Button>
        </form>

        {/* 로그인 링크 */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            이미 계정이 있으신가요?{" "}
            <Link
              href="/login"
              className="font-medium text-primary hover:underline"
            >
              로그인하기
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
